import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/**
 * 파트너 구독 등급
 * @description 파트너의 구독 등급을 나타냄
 * - PRIME_PLUS: 프라임 플러스
 * - PRIME: 프라임
 * - PRO: 프로
 * - BASE: 베이스
 * - NONE: 등급 없음
 */
export const partnerGradeSchema = z.enum([
  'PRIME_PLUS',
  'PRIME',
  'PRO',
  'BASE',
  'NONE',
]);
export type PartnerGrade = z.infer<typeof partnerGradeSchema>;

/**
 * 견적 유형
 * @description 견적 요청 방식
 * - COMPARE: 비교견적 (최대 10명 파트너 배정)
 * - ONE_TO_ONE: 1:1 견적 (지정 파트너에게 직접 요청)
 * - GRADE_BASED: 등급 기반 견적
 */
export const quoteTypeSchema = z.enum(['COMPARE', 'ONE_TO_ONE', 'GRADE_BASED']);
export type QuoteType = z.infer<typeof quoteTypeSchema>;

/**
 * 견적 요청 상태
 * @description 견적 수락 후 상태
 * - WAITING: 대기중
 * - CONSULTING: 상담중 (견적 수락 후)
 * - CLOSED: 종료
 */
export const quoteRequestStatusSchema = z.enum([
  'WAITING',
  'CONSULTING',
  'CLOSED',
]);
export type QuoteRequestStatus = z.infer<typeof quoteRequestStatusSchema>;

/**
 * 사업자 유형
 * @description 요청자의 사업자 유형
 * - INDIVIDUAL_GENERAL: 개인 일반
 * - INDIVIDUAL_SIMPLIFIED: 개인 간이
 * - CORPORATION: 법인
 * - OTHER: 기타
 */
export const businessOwnerTypeSchema = z.enum([
  'INDIVIDUAL_GENERAL',
  'INDIVIDUAL_SIMPLIFIED',
  'CORPORATION',
  'OTHER',
]);
export type BusinessOwnerType = z.infer<typeof businessOwnerTypeSchema>;

/**
 * 서비스 유형
 * @description 현재 TAX(세무) 고정
 * - TAX: 세무 서비스
 */
export const serviceTypeSchema = z.enum(['TAX']);
export type ServiceType = z.infer<typeof serviceTypeSchema>;

/**
 * 문의 유형 (세무이지)
 * @description 세무 관련 문의 유형
 * - BOOKKEEPING: 세무기장
 * - VAT_FILING: 부가세 신고 대행 (1회성)
 * - INCOME_TAX: 종합소득세
 * - TRANSFER_TAX: 양도소득세
 * - INHERITANCE_GIFT_TAX: 상속/증여세
 * - TAX_EXEMPT_FILING: 면세 신고
 * - OTHER: 기타
 */
export const inquiryTypeSchema = z.enum([
  'BOOKKEEPING',
  'VAT_FILING',
  'INCOME_TAX',
  'TRANSFER_TAX',
  'INHERITANCE_GIFT_TAX',
  'TAX_EXEMPT_FILING',
  'OTHER',
]);
export type InquiryType = z.infer<typeof inquiryTypeSchema>;

/**
 * 연매출 구간
 * @description 요청자의 연간 매출 구간
 * - UNDER_50M: 5천만원 미만
 * - UNDER_100M: 1억 미만
 * - UNDER_300M: 3억 미만
 * - UNDER_500M: 5억 미만
 * - UNDER_1B: 10억 미만
 * - OVER_1B: 10억 이상
 * - NOT_SELECTED: 미선택
 */
export const annualRevenueSchema = z.enum([
  'UNDER_50M',
  'UNDER_100M',
  'UNDER_300M',
  'UNDER_500M',
  'UNDER_1B',
  'OVER_1B',
  'NOT_SELECTED',
]);
export type AnnualRevenue = z.infer<typeof annualRevenueSchema>;

/**
 * 요청자 업종
 * @description 요청자의 업종 분류
 * - RESTAURANT: 음식점
 * - TRADE: 도소매
 * - PROFESSIONAL_SERVICE: 전문 서비스
 * - REAL_ESTATE_CONSTRUCTION: 부동산/건설
 * - ACCOMMODATION: 숙박
 * - OTHER: 기타
 */
export const requesterIndustrySchema = z.enum([
  'RESTAURANT',
  'TRADE',
  'PROFESSIONAL_SERVICE',
  'REAL_ESTATE_CONSTRUCTION',
  'ACCOMMODATION',
  'OTHER',
]);
export type RequesterIndustry = z.infer<typeof requesterIndustrySchema>;

// ============================================================================
// 견적 요청 관련 스키마
// ============================================================================

/**
 * 견적 요청 생성 Request 스키마
 * @description 견적 요청 생성(B4) 요청 바디
 * @see POST /api/market-place/partners/quote-requests
 */
export const createQuoteRequestSchema = z.object({
  /** 연 매출 구간 (UNDER_50M, UNDER_100M, ...) */
  annualRevenue: annualRevenueSchema,
  /** 사업장 주소 */
  businessAddress: z.string().optional(),
  /** 사업장 상세주소 */
  businessDetailAddress: z.string().optional(),
  /** 사업자 유형 (INDIVIDUAL_GENERAL, INDIVIDUAL_SIMPLIFIED, CORPORATION, OTHER) */
  businessOwnerType: businessOwnerTypeSchema,
  /** 사업장 지역 코드 (법정동 코드 10자리) */
  businessRegionCode: z.string(),
  /** 사업장 우편번호 */
  businessZipCode: z.string().optional(),
  /** 문의 유형 (BOOKKEEPING, VAT_FILING, ...) */
  inquiryType: inquiryTypeSchema,
  /** 견적 유형 (COMPARE: 비교견적, ONE_TO_ONE: 1:1 견적) */
  quoteType: quoteTypeSchema,
  /** 요청 내용 (자유 입력) */
  requestContent: z.string().optional(),
  /** 요청자 업종 (RESTAURANT, TRADE, ...) */
  requesterIndustry: requesterIndustrySchema,
  /** 요청자 이름 */
  requesterName: z.string(),
  /** 요청자 연락처 */
  requesterPhone: z.string(),
  /** 서비스 유형 (현재 TAX 고정) */
  serviceType: serviceTypeSchema,
  /** 대상 파트너 ID 목록 (1:1 견적 시 지정, 비교견적은 빈 배열) */
  targetPartnerIds: z.array(z.number()),
  /** 파트너 등급 필터 (GRADE_BASED 견적 시 사용) */
  grade: partnerGradeSchema.optional(),
});
export type CreateQuoteRequest = z.infer<typeof createQuoteRequestSchema>;
